import { CliError, CliErrorCode } from '@wix/cli-error';
import { readJson, writeJson } from '@wix/cli-fs';
import { join, relative } from 'node:path';
import { matcher } from 'variant';
import { EOL } from 'node:os';
import { z } from 'zod';
import { writeFile } from 'node:fs/promises';
import type { PackageJson } from 'type-fest';
import { runCommand } from '../shell/index.js';
import type { IPackageManager } from '../types.js';
import type { RepoType } from '../repo-type.js';

type YarnVersion = 'yarn' | 'yarn-berry';

export async function resolveYarnVersion(cwd: string) {
  const stdout = await runCommand('yarn --version', { cwd });
  return stdout.trim();
}

export class Yarn implements IPackageManager {
  constructor(
    private readonly repoType: RepoType,
    readonly name: YarnVersion
  ) {}

  getUsage(name: string): string {
    return `yarn create ${name.replace('create-', '')}`;
  }

  getInstallCmd(): string {
    return 'yarn install';
  }

  getRunCmd(): string {
    return 'yarn';
  }

  getRunBinaryCmd(args: string[]): { file: string; args: string[] } {
    return {
      file: 'yarn',
      args: ['run'].concat(args),
    };
  }

  async setup(packageFolder: string): Promise<void> {
    return matcher(this.repoType)
      .when(['None', 'Polyrepo'], async () => {
        if (this.name === 'yarn-berry') {
          // if yarn berry encounters a package.json in a parent folder, it
          // complains that our project needs to be part of that parent project.
          // An official way to tell yarn that this is a standalone project is
          // to create an empty lockfile.
          // A similar thing was done in create-astro: https://github.com/withastro/astro/pull/8028
          await writeFile(join(packageFolder, 'yarn.lock'), '');
        }
      })
      .when('Monorepo', async ({ rootDir }) => {
        try {
          await addPackageToWorkspaces({
            rootDir,
            packageFolder,
            yarnVersion: this.name,
          });
        } catch (error) {
          throw new CliError({
            code: CliErrorCode.FailedAddingPackageToWorkspace(),
            cause: error,
          });
        }
      })
      .complete();
  }

  async runInstall(cwd: string): Promise<void> {
    try {
      await runCommand(this.getInstallCmd(), { cwd });
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToInstallPackages(),
        info: { packageManager: this.name },
        cause: e,
      });
    }
  }

  async runInstallPackage(cwd: string, packageName: string): Promise<void> {
    try {
      await runCommand(`yarn add ${packageName}`, { cwd });
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToInstallPackage({ packageName }),
        info: { packageManager: this.name },
        cause: e,
      });
    }
  }

  async runUninstallPackage(cwd: string, packageName: string): Promise<void> {
    try {
      await runCommand(`yarn remove ${packageName}`, { cwd });
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToUninstallPackage({ packageName }),
        info: { packageManager: this.name },
        cause: e,
      });
    }
  }
}

async function getPackagesRelativePaths(
  rootDir: string,
  yarnVersion: YarnVersion
): Promise<string[]> {
  const workspacesSchema = z.array(
    z.object({
      location: z.string(),
    })
  );

  if (yarnVersion === 'yarn-berry') {
    const packages = await runCommand('yarn workspaces list --json', {
      cwd: rootDir,
    });

    const parsedPackages = workspacesSchema.parse(
      packages
        .split(EOL)
        .filter(Boolean)
        .map((pkg) => JSON.parse(pkg))
    );

    return parsedPackages.map((pkg) => pkg.location);
  }

  const packages = await runCommand('yarn --json workspaces info', {
    cwd: rootDir,
  });

  const parsedPackages = workspacesSchema.parse(
    Object.values(JSON.parse(JSON.parse(packages).data))
  );
  return parsedPackages.map((pkg) => pkg.location);
}

async function addPackageToWorkspaces({
  rootDir,
  packageFolder,
  yarnVersion,
}: {
  rootDir: string;
  packageFolder: string;
  yarnVersion: YarnVersion;
}) {
  const relativePackagePath = relative(rootDir, packageFolder);
  const packages = await getPackagesRelativePaths(rootDir, yarnVersion);

  if (packages.includes(relativePackagePath)) {
    return;
  }

  const packageJson = (await readJson(
    join(rootDir, 'package.json')
  )) as PackageJson;

  if (Array.isArray(packageJson.workspaces)) {
    packageJson.workspaces.push(relativePackagePath);
  } else if (typeof packageJson.workspaces === 'object') {
    packageJson.workspaces = {
      ...packageJson.workspaces,
      packages: [
        ...(packageJson.workspaces.packages ?? []),
        relativePackagePath,
      ],
    };
  } else {
    throw new Error(
      `Failed to read workspaces structure. Expected an array or object but got ${JSON.stringify(
        packageJson.workspaces
      )}`
    );
  }

  await writeJson(join(rootDir, 'package.json'), packageJson, { spaces: 2 });
}
